import {
  FacetHit,
  Hit,
  MultipleQueriesQuery,
  MultipleQueriesResponse,
  SearchForFacetValuesQueryParams,
  SearchForFacetValuesResponse,
  SearchOptions,
} from '@algolia/client-search';
import {
  ApolloClient,
  FetchPolicy,
  TypedDocumentNode,
  gql,
  useApolloClient,
} from '@apollo/client';
import { ComponentProps, useMemo } from 'react';
import { InstantSearch } from 'react-instantsearch';

import {
  SearchFacetDefinitionInput,
  SearchRefinementInput,
  SearchSortDirection,
  SearchSortInput,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { AlgoliaCardIndexesName } from '@sorare/core/src/contexts/config';
import { SorareSearchClient } from '@sorare/core/src/hooks/search/useSearchClient';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { isType } from '@sorare/core/src/lib/gql';

import { extractQueryGeneric, extractVariableGeneric } from 'lib/searchClient';

import type { useCardsSearchClient_searchCardFacetValues } from './__generated__/useCardsSearchClient.graphql';
import type {
  GlobalCardsSearchFacetValuesQuery,
  GlobalCardsSearchFacetValuesQueryVariables,
  GlobalCardsSearchQuery,
  GlobalCardsSearchQueryVariables,
  GlobalGroupedCardsSearchQuery,
  GlobalGroupedCardsSearchQueryVariables,
  useCardsSearchClient_searchCardHit,
  useCardsSearchClient_searchInterface,
} from './__generated__/useGlobalCardsSearchClient.graphql';

type SortFields = Record<
  string,
  {
    field: string | Record<Sport, string>;
    direction: SearchSortDirection;
  }[]
>;

const CARD_ON_SALE_SORT_FIELDS: SortFields = {
  'Ending Soon': [
    {
      field: 'sale.end_date',
      direction: SearchSortDirection.ASC,
    },
  ],
  'Newly Listed': [
    {
      field: 'sale.start_date',
      direction: SearchSortDirection.DESC,
    },
  ],
  'Highest Price': [
    {
      field: 'sale.price',
      direction: SearchSortDirection.DESC,
    },
  ],
  'Lowest Price': [
    {
      field: 'sale.price',
      direction: SearchSortDirection.ASC,
    },
  ],
  'Highest Average Score': [
    {
      field: {
        [Sport.FOOTBALL]: 'so5.last_fifteen_so5_average_score',
        [Sport.BASEBALL]: 'so5.last_fifteen_so5_average_score',
        [Sport.NBA]: 'so5.last_ten_played_so5_average_score',
      },
      direction: SearchSortDirection.DESC,
    },
  ],
  'Best Value': [
    {
      field: 'sale.type_ranked',
      direction: SearchSortDirection.ASC,
    },
    { field: 'open_for_game_stats', direction: SearchSortDirection.DESC },
    { field: 'value_for_money', direction: SearchSortDirection.DESC },
  ],
  'Popular Player': [
    {
      field: 'open_for_game_stats',
      direction: SearchSortDirection.DESC,
    },
    { field: 'popularity', direction: SearchSortDirection.DESC },
    { field: 'player.slug', direction: SearchSortDirection.ASC },
  ],
  Popularity: [
    {
      field: 'open_for_game_stats',
      direction: SearchSortDirection.DESC,
    },
    { field: 'popularity', direction: SearchSortDirection.DESC },
    { field: 'player.slug', direction: SearchSortDirection.ASC },
  ],
};

const BLOCKCHAIN_CARDS_SORT_FIELDS: SortFields = {
  'Highest Price': [
    {
      field: 'on_sale',
      direction: SearchSortDirection.DESC,
    },
    {
      field: 'price',
      direction: SearchSortDirection.DESC,
    },
  ],
  'Lowest Price': [
    {
      field: 'on_sale',
      direction: SearchSortDirection.DESC,
    },
    {
      field: 'price',
      direction: SearchSortDirection.ASC,
    },
  ],
  'Highest Average Score': [
    {
      field: {
        [Sport.FOOTBALL]: 'so5.last_fifteen_so5_average_score',
        [Sport.BASEBALL]: 'so5.last_fifteen_so5_average_score',
        [Sport.NBA]: 'so5.last_ten_played_so5_average_score',
      },
      direction: SearchSortDirection.DESC,
    },
  ],
  'Best Value': [
    {
      field: 'sale.type_ranked',
      direction: SearchSortDirection.ASC,
    },
    { field: 'open_for_game_stats', direction: SearchSortDirection.DESC },
    { field: 'value_for_money', direction: SearchSortDirection.DESC },
  ],
  'Popular Player': [
    { field: 'open_for_game_stats', direction: SearchSortDirection.DESC },
    { field: 'popularity', direction: SearchSortDirection.DESC },
    { field: 'player.slug', direction: SearchSortDirection.ASC },
  ],
  Popularity: [
    { field: 'open_for_game_stats', direction: SearchSortDirection.DESC },
    { field: 'popularity', direction: SearchSortDirection.DESC },
    { field: 'player.slug', direction: SearchSortDirection.ASC },
  ],
  'Newly Listed': [
    {
      field: 'on_sale',
      direction: SearchSortDirection.DESC,
    },
    { field: 'sale.start_date', direction: SearchSortDirection.DESC },
  ],
  'Ending Soon': [
    {
      field: 'on_sale',
      direction: SearchSortDirection.DESC,
    },
    { field: 'sale.end_date', direction: SearchSortDirection.ASC },
  ],
};
type SearchClient = ComponentProps<typeof InstantSearch>['searchClient'];

type BaseQueryVariables = {
  query: string;
  page: number;
  pageSize: number;
  sorts: SearchSortInput[];
  advancedFilters?: string | null;
  refinements: SearchRefinementInput[];
  facets: SearchFacetDefinitionInput[];
  onSaleOnly: boolean;
};

type GroupedQueryVariables = BaseQueryVariables & {
  groupBy: string[];
  groupLimit: number;
  cheapestWithinGroup: boolean;
};

type BaseQuery<Variables extends BaseQueryVariables> = TypedDocumentNode<
  {
    searchCards: useCardsSearchClient_searchInterface;
  },
  Variables
>;

type GroupedQuery<Variables extends GroupedQueryVariables> = TypedDocumentNode<
  {
    searchGroupedCards: useCardsSearchClient_searchInterface;
  },
  Variables
>;

type BaseFacetValuesQueryVariables = {
  query: string;
  field: string;
  facetQuery: string;
  advancedFilters?: string | null;
  refinements: SearchRefinementInput[];
  onSaleOnly: boolean;
};
type BaseFacetValuesQuery<Variables extends BaseFacetValuesQueryVariables> =
  TypedDocumentNode<
    {
      searchCardFacetValues: useCardsSearchClient_searchCardFacetValues[];
    },
    Variables
  >;

type Options = {
  fetchPolicy?: FetchPolicy;
  onSaleOnly: boolean;
  sport: Sport;
  variables?: Record<string, unknown>;
  groupBy?: string[];
  groupLimit?: number;
  cheapestWithinGroup?: boolean;
};

const getHitsFromResponse = <THit>(
  res: useCardsSearchClient_searchInterface
): THit[] => {
  if (isType(res, 'SearchGroupedCards')) {
    return res.groups.map(group => ({
      ...(group.hits[0] &&
        ({
          ...group.hits[0],
          sale: {
            ...group.hits[0].sale,
            distinct_key: group.hits[0].sale?.distinctKey || undefined,
          },
        } as THit)),
      objectID: group.hits[0].slug,
      _distinctSeqID: 0,
      distinctKeyHitCount: group.nbHits,
    }));
  }

  if (isType(res, 'SearchCardHits')) {
    return res.hits.map(hit => ({
      ...({
        ...hit,
        sale: { ...hit.sale, distinct_key: hit.sale?.distinctKey },
      } as THit),
      objectID: hit.slug,
    }));
  }

  return res.hitIds.map(hitId => ({
    ...({ slug: hitId } as THit),
    objectID: hitId,
  }));
};

abstract class BaseSorareSearchClient<
    TFacetValuesQuery extends BaseFacetValuesQuery<TFacetValuesVariables>,
    TFacetValuesVariables extends
      BaseFacetValuesQueryVariables = extractVariableGeneric<TFacetValuesQuery>,
  >
  extends SorareSearchClient
  implements SearchClient
{
  protected client: ApolloClient<object>;

  private facetValuesQuery: TFacetValuesQuery;

  protected options: Options;

  constructor(
    client: ApolloClient<object>,
    facetValuesQuery: TFacetValuesQuery,
    options: Options
  ) {
    super();
    this.client = client;
    this.facetValuesQuery = facetValuesQuery;
    this.options = options;
  }

  protected toSorts(indexName: string, sport: Sport): SearchSortInput[] {
    let sortFields: {
      field: string | Record<Sport, string>;
      direction: SearchSortDirection;
    }[] = [];
    if (this.options.onSaleOnly) {
      sortFields =
        CARD_ON_SALE_SORT_FIELDS[indexName as AlgoliaCardIndexesName];
      if (sortFields === undefined) {
        sortFields = CARD_ON_SALE_SORT_FIELDS['Ending Soon'];
      }
    } else {
      sortFields =
        BLOCKCHAIN_CARDS_SORT_FIELDS[indexName as AlgoliaCardIndexesName];
      if (sortFields === undefined) {
        sortFields = BLOCKCHAIN_CARDS_SORT_FIELDS.Popularity;
      }
    }

    if (sortFields) {
      return sortFields.map(s => {
        const field = typeof s.field === 'string' ? s.field : s.field[sport];
        return { field, direction: s.direction };
      });
    }
    return [];
  }

  protected abstract getCards(
    indexName: string,
    params: NonNullable<MultipleQueriesQuery['params']>
  ): Promise<useCardsSearchClient_searchInterface>;

  async search<THit>(queries: readonly MultipleQueriesQuery[]) {
    // only the main query is important, all others are for the disjunctive facets and are handled natively
    const query = queries[0];
    const params = query.params!;
    const res = await this.getCards(query.indexName, params);

    const hits = getHitsFromResponse<Hit<THit>>(res);

    const response: MultipleQueriesResponse<THit> = {
      results: [
        {
          hits,
          facets: Object.fromEntries(
            res.facets.map(
              (facet: {
                field: string;
                values: Array<{ value: string; count: number }>;
              }) => [
                facet.field,
                Object.fromEntries(
                  facet.values.map((v: { value: string; count: number }) => [
                    v.value,
                    v.count,
                  ])
                ),
              ]
            )
          ),
          facets_stats: Object.fromEntries(
            res.facets.map(
              (facet: {
                field: string;
                min: { value: string } | null;
                max: { value: string } | null;
              }) => [
                facet.field,
                {
                  min: parseInt(facet.min?.value || '0', 10),
                  max: parseInt(facet.max?.value || '0', 10),
                  avg: 0,
                  sum: 0,
                },
              ]
            )
          ),
          page: res.page,
          nbPages: res.nbPages,
          nbHits: res.nbHits,
          hitsPerPage: params.hitsPerPage!,
          processingTimeMS: 0,
          query: params.query!,
          exhaustiveNbHits: true,
          params: '',
          index: query.indexName,
        },
      ],
    };

    return response;
  }

  async searchForFacetValues(
    queries: ReadonlyArray<{
      readonly indexName: string;
      readonly params: SearchForFacetValuesQueryParams & SearchOptions;
    }>
  ) {
    // only the first query is handled - which should cover all use-cases
    const query = queries[0];
    const params = query.params!;

    const { data } = await this.client.query<
      extractQueryGeneric<TFacetValuesQuery>,
      TFacetValuesVariables
    >({
      query: this.facetValuesQuery,
      variables: {
        query: params.query || '',
        field: params.facetName,
        facetQuery: params.facetQuery || '',
        advancedFilters: params.filters,
        refinements: this.toRefinements(
          params.facetFilters as string[][],
          params.numericFilters as string[]
        ),
        onSaleOnly: this.options.onSaleOnly,
      } as TFacetValuesVariables,
    });

    const facetHits: FacetHit[] = data.searchCardFacetValues.map(v => ({
      value: v.value,
      count: v.count,
      highlighted: '',
    }));

    const response: SearchForFacetValuesResponse[] = [
      {
        facetHits,
        exhaustiveFacetsCount: true,
      },
    ];
    return response;
  }
}

class GlobalCardsSorareSearchClient<
    TQuery extends BaseQuery<TVariables>,
    TFacetValuesQuery extends BaseFacetValuesQuery<TFacetValuesVariables>,
    TVariables extends BaseQueryVariables = extractVariableGeneric<TQuery>,
    TFacetValuesVariables extends
      BaseFacetValuesQueryVariables = extractVariableGeneric<TFacetValuesQuery>,
  >
  extends BaseSorareSearchClient<TFacetValuesQuery>
  implements SearchClient
{
  private query: TQuery;

  constructor(
    client: ApolloClient<object>,
    query: TQuery,
    facetValuesQuery: TFacetValuesQuery,
    options: Pick<Options, 'fetchPolicy' | 'onSaleOnly' | 'sport' | 'variables'>
  ) {
    super(client, facetValuesQuery, options);
    this.query = query;
  }

  protected async getCards(
    indexName: string,
    params: NonNullable<MultipleQueriesQuery['params']>
  ): Promise<useCardsSearchClient_searchInterface> {
    const { data } = await this.client.query<
      extractQueryGeneric<TQuery>,
      TVariables
    >({
      query: this.query,
      fetchPolicy: this.options.fetchPolicy,
      variables: {
        query: params.query || '',
        page: (params.page || 0) + 1, // 1-based
        pageSize: params.hitsPerPage || 1,
        sorts: this.toSorts(indexName, this.options.sport),
        advancedFilters: params.filters,
        refinements: this.toRefinements(
          params.facetFilters as string[][],
          params.numericFilters as string[] | undefined
        ),
        facets: this.toFacets(params.facets),
        onSaleOnly: this.options.onSaleOnly,
        ...(this.options.variables || {}),
      } as TVariables,
    });

    return data.searchCards;
  }
}

class GlobalGroupedCardsSorareSearchClient<
    TQuery extends GroupedQuery<TVariables>,
    TFacetValuesQuery extends BaseFacetValuesQuery<TFacetValuesVariables>,
    TVariables extends GroupedQueryVariables = extractVariableGeneric<TQuery>,
    TFacetValuesVariables extends
      BaseFacetValuesQueryVariables = extractVariableGeneric<TFacetValuesQuery>,
  >
  extends BaseSorareSearchClient<TFacetValuesQuery>
  implements SearchClient
{
  private query: TQuery;

  constructor(
    client: ApolloClient<object>,
    query: TQuery,
    facetValuesQuery: TFacetValuesQuery,
    options: Options
  ) {
    super(client, facetValuesQuery, options);
    this.query = query;
  }

  protected async getCards(
    indexName: string,
    params: NonNullable<MultipleQueriesQuery['params']>
  ) {
    const { data } = await this.client.query<
      extractQueryGeneric<TQuery>,
      TVariables
    >({
      query: this.query,
      fetchPolicy: this.options.fetchPolicy,
      variables: {
        query: params.query || '',
        page: (params.page || 0) + 1, // 1-based
        pageSize: params.hitsPerPage || 1,
        sorts: this.toSorts(indexName, this.options.sport),
        advancedFilters: params.filters,
        refinements: this.toRefinements(
          params.facetFilters as string[][],
          params.numericFilters as string[] | undefined
        ),
        facets: this.toFacets(params.facets),
        groupBy: this.options.groupBy,
        groupLimit: this.options.groupLimit,
        onSaleOnly: this.options.onSaleOnly,
        cheapestWithinGroup: this.options.cheapestWithinGroup,
        ...(this.options.variables || {}),
      } as TVariables,
    });
    return data.searchGroupedCards;
  }
}

const useGroupedCardsSearchClient = <
  TQuery extends GroupedQuery<TVariables>,
  TFacetValuesQuery extends BaseFacetValuesQuery<TFacetValuesVariables>,
  TVariables extends GroupedQueryVariables = extractVariableGeneric<TQuery>,
  TFacetValuesVariables extends
    BaseFacetValuesQueryVariables = extractVariableGeneric<TFacetValuesQuery>,
>(
  query: TQuery,
  facetValuesQuery: TFacetValuesQuery,
  options: Options &
    Required<Pick<Options, 'groupBy' | 'groupLimit' | 'cheapestWithinGroup'>>
): SearchClient => {
  const apolloClient = useApolloClient();
  const optionsString = JSON.stringify(options);

  const searchClient: SearchClient = useMemo(() => {
    return new GlobalGroupedCardsSorareSearchClient<TQuery, TFacetValuesQuery>(
      apolloClient,
      query,
      facetValuesQuery,
      options
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apolloClient, query, facetValuesQuery, optionsString]);

  return searchClient;
};

const useCardsSearchClient = <
  TQuery extends BaseQuery<TVariables>,
  TFacetValuesQuery extends BaseFacetValuesQuery<TFacetValuesVariables>,
  TVariables extends BaseQueryVariables = extractVariableGeneric<TQuery>,
  TFacetValuesVariables extends
    BaseFacetValuesQueryVariables = extractVariableGeneric<TFacetValuesQuery>,
>(
  query: TQuery,
  facetValuesQuery: TFacetValuesQuery,
  options: Omit<Options, 'groupBy' | 'groupLimit'>
): SearchClient => {
  const apolloClient = useApolloClient();
  const optionsString = JSON.stringify(options);

  const searchClient: SearchClient = useMemo(() => {
    return new GlobalCardsSorareSearchClient<TQuery, TFacetValuesQuery>(
      apolloClient,
      query,
      facetValuesQuery,
      options
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apolloClient, query, facetValuesQuery, optionsString]);

  return searchClient;
};

const searchCardHitsFragment = gql`
  fragment useCardsSearchClient_searchCardHit on SearchCardHit {
    slug
    sale {
      distinctKey
      type
    }
  }
` as TypedDocumentNode<useCardsSearchClient_searchCardHit>;

useCardsSearchClient.fragments = {
  searchCardFacetValues: gql`
    fragment useCardsSearchClient_searchCardFacetValues on SearchFacetValue {
      value
      count
    }
  ` as TypedDocumentNode<useCardsSearchClient_searchCardFacetValues>,
  searchInterface: gql`
    fragment useCardsSearchClient_searchInterface on SearchInterface {
      page
      nbPages
      hitIds
      ... on SearchCardHits {
        hits {
          slug
          ...useCardsSearchClient_searchCardHit
        }
      }
      nbHits
      facets {
        field
        values {
          value
          count
        }
        min {
          value
        }
        max {
          value
        }
      }
      processingTimeMs {
        facetValuesFts
        faceting
        filtering
        fts
        sorting
        total
      }
      ... on SearchGroupedCards {
        groups {
          hits {
            slug
            ...useCardsSearchClient_searchCardHit
          }
          hitIds
          nbHits
        }
      }
    }
    ${searchCardHitsFragment}
  ` as TypedDocumentNode<useCardsSearchClient_searchInterface>,
};

const GLOBAL_CARDS_SEARCH_FACET_VALUES_QUERY = gql`
  query GlobalCardsSearchFacetValuesQuery(
    $field: String!
    $facetQuery: String!
    $query: String!
    $limit: Int
    $advancedFilters: String
    $refinements: [SearchRefinementInput!]!
    $onSaleOnly: Boolean
  ) {
    searchCardFacetValues(
      field: $field
      facetQuery: $facetQuery
      query: $query
      limit: $limit
      advancedFilters: $advancedFilters
      refinements: $refinements
      onSaleOnly: $onSaleOnly
    ) {
      ...useCardsSearchClient_searchCardFacetValues
    }
  }
  ${useCardsSearchClient.fragments.searchCardFacetValues}
` as TypedDocumentNode<
  GlobalCardsSearchFacetValuesQuery,
  GlobalCardsSearchFacetValuesQueryVariables
>;

const GLOBAL_CARDS_SEARCH_QUERY = gql`
  query GlobalCardsSearchQuery(
    $query: String!
    $page: Int
    $pageSize: Int
    $advancedFilters: String
    $sorts: [SearchSortInput!]!
    $refinements: [SearchRefinementInput!]!
    $facets: [SearchFacetDefinitionInput!]!
    $onSaleOnly: Boolean
  ) {
    searchCards(
      query: $query
      page: $page
      pageSize: $pageSize
      sorts: $sorts
      advancedFilters: $advancedFilters
      refinements: $refinements
      facets: $facets
      onSaleOnly: $onSaleOnly
    ) {
      ...useCardsSearchClient_searchInterface
    }
  }
  ${useCardsSearchClient.fragments.searchInterface}
` as TypedDocumentNode<GlobalCardsSearchQuery, GlobalCardsSearchQueryVariables>;

const GLOBAL_CARDS_GROUPED_SEARCH_QUERY = gql`
  query GlobalGroupedCardsSearchQuery(
    $query: String!
    $page: Int
    $pageSize: Int
    $advancedFilters: String
    $sorts: [SearchSortInput!]!
    $refinements: [SearchRefinementInput!]!
    $facets: [SearchFacetDefinitionInput!]!
    $groupBy: [String!]!
    $groupLimit: Int
    $onSaleOnly: Boolean
    $cheapestWithinGroup: Boolean
  ) {
    searchGroupedCards(
      query: $query
      page: $page
      pageSize: $pageSize
      sorts: $sorts
      advancedFilters: $advancedFilters
      refinements: $refinements
      facets: $facets
      groupBy: $groupBy
      groupLimit: $groupLimit
      onSaleOnly: $onSaleOnly
      cheapestWithinGroup: $cheapestWithinGroup
    ) {
      ...useCardsSearchClient_searchInterface
    }
  }
  ${useCardsSearchClient.fragments.searchInterface}
` as TypedDocumentNode<
  GlobalGroupedCardsSearchQuery,
  GlobalGroupedCardsSearchQueryVariables
>;

export const useGroupedGlobalCardsSearchClient = ({
  onSaleOnly,
  sport,
  groupBy,
  groupLimit = 1,
  cheapestWithinGroup = false,
}: {
  onSaleOnly: boolean;
  sport: Sport;
  groupBy: string[];
  groupLimit?: number;
  cheapestWithinGroup?: boolean;
}) => {
  const {
    flags: { useNetworkOnlySearchClient = true },
  } = useFeatureFlags();

  const options = useMemo(
    () => ({
      onSaleOnly,
      sport,
      groupBy,
      groupLimit,
      cheapestWithinGroup,
      fetchPolicy: useNetworkOnlySearchClient
        ? ('network-only' as const)
        : undefined,
    }),
    [
      onSaleOnly,
      sport,
      groupBy,
      groupLimit,
      cheapestWithinGroup,
      useNetworkOnlySearchClient,
    ]
  );

  return useGroupedCardsSearchClient<
    typeof GLOBAL_CARDS_GROUPED_SEARCH_QUERY,
    typeof GLOBAL_CARDS_SEARCH_FACET_VALUES_QUERY
  >(
    GLOBAL_CARDS_GROUPED_SEARCH_QUERY,
    GLOBAL_CARDS_SEARCH_FACET_VALUES_QUERY,
    options
  );
};

export const useGlobalCardsSearchClient = ({
  onSaleOnly,
  sport,
}: {
  onSaleOnly: boolean;
  sport: Sport;
  groupBy?: never;
}) => {
  const {
    flags: { useNetworkOnlySearchClient = true },
  } = useFeatureFlags();

  const options = useMemo(
    () => ({
      onSaleOnly,
      sport,
      fetchPolicy: useNetworkOnlySearchClient
        ? ('network-only' as const)
        : undefined,
    }),
    [onSaleOnly, sport, useNetworkOnlySearchClient]
  );

  return useCardsSearchClient<
    typeof GLOBAL_CARDS_SEARCH_QUERY,
    typeof GLOBAL_CARDS_SEARCH_FACET_VALUES_QUERY
  >(GLOBAL_CARDS_SEARCH_QUERY, GLOBAL_CARDS_SEARCH_FACET_VALUES_QUERY, options);
};
